/**
 * Fetch and join the server's per-line cart prices onto cart rows.
 *
 * The cart RPC (Account.update_cart_quantity, api/src/models/account.py)
 * returns `prices`: one entry per priced line with the line's identity and
 * `unit_price_cents` resolved at the line's REAL quantity. Showing that
 * number -- instead of a quantity-1 quote multiplied out in the browser -- is
 * what keeps the cart equal to the charge once a line reaches a quantity
 * break.
 *
 * The key mirrors the server's: ids compare as strings, an absent artwork id
 * is null (never the string "null"), and `customized_product_id` is part of
 * the identity -- an Artifi customized line has null colour and size and is
 * identified by it, so two customized lines of one product must not collide,
 * and a regular line must never match a customized entry (#3888).
 */
import { SUPPRESS_ERROR_TOAST } from "@/interceptors/authResponseInterceptor";
import { cartStoreArgs } from "@/router/storefrontStoreId";

/**
 * A cart line's logo, normalized for identity comparison: the server's
 * `_artwork_key` (api/src/models/account.py). The id is stored verbatim from
 * untyped JSON, so the same logo can be `5` or `"5"`; an absent id is null.
 * Also used by the cart's quantity/remove handlers and the PDP merge base,
 * so "which line is this" has one answer on the client.
 */
export function artworkKey(value) {
  return value === null || value === undefined ? null : String(value);
}

function lineKey(line) {
  return JSON.stringify([
    String(line.campaign_product_id),
    line.primary_attribute_value ?? null,
    line.secondary_attribute_value ?? null,
    artworkKey(line.selected_artwork_id),
    artworkKey(line.customized_product_id),
  ]);
}

/**
 * What to tell the shopper about a failed `cart_prices` call.
 *
 * A 409 PriceUnavailable names the product and size that has no price --
 * the shopper must remove that line, and Retry cannot fix it -- so for the
 * shopper-safe statuses (409, 400) the server's text is shown: a JSON:API
 * `errors[0].detail` / `title`, then a flat `message`. Everything else (a
 * 5xx, another status, a network error, an empty body) gets
 * CART_PRICES_GENERIC_ERROR, so internal error text never reaches a shopper.
 */
export function cartPricesErrorMessage(err) {
  const response = err && err.response ? err.response : null;
  if (!response || !SHOPPER_SAFE_STATUSES.has(response.status)) {
    return CART_PRICES_GENERIC_ERROR;
  }
  const data = response.data;
  if (!data || typeof data !== "object") return CART_PRICES_GENERIC_ERROR;
  const first = Array.isArray(data.errors) ? data.errors[0] : null;
  const text =
    (first && (first.detail || first.title)) || data.message || null;
  return typeof text === "string" && text.trim()
    ? text.trim()
    : CART_PRICES_GENERIC_ERROR;
}

/** What a shopper sees when the server's own text is not safe to show. */
export const CART_PRICES_GENERIC_ERROR = "We couldn't load prices. Try again.";

// The statuses whose server text is written FOR a shopper: 409
// PriceUnavailable ("<product> has no price set for size <x>", "<product>
// is not offered in size <x>") and 400 (the line/quantity caps). A 5xx or
// any other status can carry internal detail (exception text, hosts), so it
// is never shown (#3884, tf3 fault test).
const SHOPPER_SAFE_STATUSES = new Set([400, 409]);

/** Build a lookup from the RPC's `prices` array (tolerates junk). */
export function indexLinePrices(prices) {
  const index = new Map();
  if (!Array.isArray(prices)) return index;
  for (const entry of prices) {
    if (
      entry &&
      entry.campaign_product_id != null &&
      Number.isInteger(entry.unit_price_cents)
    ) {
      index.set(lineKey(entry), entry.unit_price_cents);
    }
  }
  return index;
}

/** The server's unit price in cents for this cart line, or undefined. */
export function lineUnitCents(index, cartLine) {
  if (!index || !cartLine || cartLine.campaign_product_id == null) {
    return undefined;
  }
  return index.get(lineKey(cartLine));
}

/**
 * Fetch this account's per-line cart prices from the READ-ONLY
 * `cart_prices` Account RPC and return the lookup.
 *
 * The RPC returns the same `prices` array `update_cart_quantity` returns,
 * scoped to the store named. THROWS on any failure -- no account, no
 * resolved store (no request is sent), a network error, a response without
 * a `prices` array -- so a caller cannot mistake "could not price" for
 * "priced nothing". Callers show an error with Retry and block ordering;
 * none may substitute a price of their own.
 *
 * Shared by the storefront cart (StorefrontCart.vue) and the staff
 * Add New Order cart (Order/Cart.vue). `storeId` is the store the cart is for: the
 * gate's id on the storefront, the store being ordered for in the back
 * office (`cartStoreArgs`).
 */
export async function fetchCartLinePrices(http, account, storeId) {
  const accountId = account && account._jv ? account._jv.id : null;
  if (accountId == null) {
    throw new Error("no account to price the cart for");
  }
  // The callers render their OWN error and Retry for this request, so the
  // global error toast is suppressed: one error surface, not two.
  const response = await http.post(
    `/accounts/${accountId}/cart_prices`,
    { meta: { method: "cart_prices", args: cartStoreArgs(storeId) } },
    { [SUPPRESS_ERROR_TOAST]: true }
  );
  const result =
    response && response.data && response.data.meta
      ? response.data.meta.result
      : undefined;
  const prices = result ? result.prices : undefined;
  if (!Array.isArray(prices)) {
    throw new Error("cart_prices returned no prices");
  }
  return indexLinePrices(prices);
}

